import React, { useEffect, useRef } from 'react';
import { FileTextIcon } from 'lucide-react';
import { educationData, skillsData } from '../utils/constants';
const About = ({
  language = 'en'
}) => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.fade-in');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const translations = {
    en: {
      about: 'About',
      me: 'Me',
      bio1: "I'm a sophomore at Northeastern University studying finance with a minor in data science. Coming from a first-generation background, I've always been motivated to make the most of the opportunities in front of me. My academic focus has been matched by hands-on experience in finance, from equity research and private equity to real estate acquisitions and FX sales & trading. Along the way, I've received a $364,000 scholarship from Northeastern University and earned recognition on the Dean's List.",
      bio2: "Professionally, I've worked with organizations including Orient Commercial Bank, HFIC, Cavalina Property Group, Westlake Securities, Springer Capital, HP Tech Ventures, and VinaCapital, where I've built skills in valuation, research, and financial analysis. As Deputy Chief External Relations Officer at Vu A Dinh Scholarship Fund, I handled $18M in donations and maintained relationships with 120+ corporate partners. I founded AidWorks Foundation, a nonprofit that raises more than $13,500 each year to support community initiatives and serves 3,500+ individuals through 100+ volunteers. I'm also exploring entrepreneurship through Airevo LLC, a real estate technology venture. As Vice President of the Northeastern Investment Banking Group, I've helped grow the club to 30+ members with alumni at 10+ companies. These experiences have shaped my interest in investment banking and private equity, and I'm eager to continue learning and contributing in meaningful ways.",
      education: 'Education',
      minor: 'Minor:',
      scholarship: '$364K Scholarship |',
      skills: 'Skills',
      languages: 'Languages:',
      technical: 'Technical:',
      interests: 'Interests:',
      downloadResume: 'Download Resume',
      featuredStockPitch: 'Featured Stock Pitch'
    },
    vi: {
      about: 'Giới',
      me: 'Thiệu',
      bio1: 'Tôi là sinh viên năm hai tại Đại học Northeastern, chuyên ngành Tài chính với chuyên ngành phụ về Khoa học dữ liệu. Là người thế hệ đầu tiên, tôi luôn có động lực để tận dụng tối đa các cơ hội trước mắt. Sự tập trung học thuật của tôi đã được kết hợp với kinh nghiệm thực tế trong lĩnh vực tài chính, từ nghiên cứu cổ phiếu và vốn cổ phần tư nhân đến mua bán bất động sản và kinh doanh & bán hàng FX. Trong quá trình đó, tôi đã nhận được học bổng $364,000 từ Đại học Northeastern và được ghi nhận trong Danh sách Hiệu Trưởng.',
      bio2: 'Về mặt chuyên môn, tôi đã làm việc với các tổ chức bao gồm Ngân hàng Thương mại Phương Đông, HFIC, Cavalina Property Group, Westlake Securities, Springer Capital, HP Tech Ventures và VinaCapital, nơi tôi đã xây dựng kỹ năng về định giá, nghiên cứu và phân tích tài chính. Với vai trò là Phó Giám đốc Đối ngoại tại Quỹ Học bổng Vừ A Dính, tôi đã xử lý 18 triệu đô la tiền quyên góp và duy trì mối quan hệ với hơn 120 đối tác doanh nghiệp. Tôi đã thành lập Quỹ AidWorks, một tổ chức phi lợi nhuận gây quỹ hơn 13.500 đô la mỗi năm để hỗ trợ các sáng kiến cộng đồng và phục vụ hơn 3.500 cá nhân thông qua hơn 100 tình nguyện viên. Tôi cũng đang khám phá tinh thần kinh doanh thông qua Airevo LLC, một doanh nghiệp công nghệ bất động sản. Với vai trò là Phó Chủ tịch của Nhóm Ngân hàng Đầu tư Northeastern, tôi đã giúp phát triển câu lạc bộ lên hơn 30 thành viên với cựu sinh viên tại hơn 10 công ty. Những kinh nghiệm này đã định hình sở thích của tôi trong lĩnh vực ngân hàng đầu tư và vốn cổ phần tư nhân, và tôi mong muốn tiếp tục học hỏi và đóng góp một cách có ý nghĩa.',
      education: 'Học Vấn',
      minor: 'Chuyên ngành phụ:',
      scholarship: 'Học bổng $364K |',
      skills: 'Kỹ Năng',
      languages: 'Ngôn ngữ:',
      technical: 'Kỹ thuật:',
      interests: 'Sở thích:',
      downloadResume: 'Tải Sơ Yếu Lý Lịch',
      featuredStockPitch: 'Bài Phân Tích Cổ Phiếu Nổi Bật'
    }
  };
  const t = translations[language];
  return <section id="about" ref={sectionRef} className="w-full py-24 bg-background relative overflow-hidden animated-gradient" data-id="element-9">
      <div className="container mx-auto px-4" data-id="element-10">
        <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-11">
          {/* Profile Image */}
          <div className="w-full md:w-1/3 flex justify-center md:justify-end fade-in mb-8 md:mb-0" data-id="element-12">
            <div className="relative w-48 h-48 sm:w-64 sm:h-64 rounded-full overflow-hidden gradient-border float-animation" data-id="element-13">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oz2JDEbs664H64BHe3SMGd/Trung_Nguyen_Design.png" alt="Trung Nguyen" className="w-full h-full object-cover object-center" style={{
              objectPosition: 'center top'
            }} data-id="element-14" />
            </div>
          </div>
          {/* About Text */}
          <div className="w-full md:w-2/3" data-id="element-15">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 fade-in text-center md:text-left" data-id="element-16">
              {t.about} <span className="gradient-text" data-id="element-17">{t.me}</span>
            </h2>
            <p className="text-muted text-lg mb-4 leading-relaxed fade-in delay-100" data-id="element-18">
              {t.bio1}
            </p>
            <p className="text-muted text-lg mb-6 leading-relaxed fade-in delay-200" data-id="element-19">
              {t.bio2}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6" data-id="element-20">
              <div className="fade-in delay-300" data-id="element-21">
                <h3 className="text-xl font-display font-bold mb-2" data-id="element-22">
                  {t.education}
                </h3>
                <p className="text-accent-cyan mb-1" data-id="element-23">{educationData.school}</p>
                <p className="text-muted mb-1" data-id="element-24">{educationData.degree}</p>
                <p className="text-muted mb-1" data-id="element-25">
                  {t.minor} {educationData.minor}
                </p>
                <p className="text-muted mb-3" data-id="element-26">
                  {t.scholarship} {educationData.dates}
                </p>
              </div>
              <div className="fade-in delay-400" data-id="element-27">
                <h3 className="text-xl font-display font-bold mb-2" data-id="element-28">
                  {t.skills}
                </h3>
                <p className="text-muted mb-1" data-id="element-29">
                  <span className="text-accent-cyan" data-id="element-30">{t.languages}</span>{' '}
                  {skillsData.languages.join(', ')}
                </p>
                <p className="text-muted mb-1" data-id="element-31">
                  <span className="text-accent-cyan" data-id="element-32">{t.technical}</span>{' '}
                  {skillsData.technical.slice(0, 5).join(', ')}
                </p>
                <p className="text-muted mb-3" data-id="element-33">
                  <span className="text-accent-cyan" data-id="element-34">{t.interests}</span>{' '}
                  {skillsData.interests.slice(0, 4).join(', ')}
                </p>
              </div>
            </div>
            <div className="text-center md:text-left" data-id="element-35">
              <a href="https://drive.google.com/file/d/1uaQqtMCqQRsS2yBadyDQ0cmCFu7Edq3t/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="gradient-button px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 shiny-button fade-in delay-500" data-id="element-36">
                <FileTextIcon size={20} data-id="element-37" />
                {t.downloadResume}
              </a>
            </div>
          </div>
        </div>
        {/* Stock Pitch Embed */}
        <div className="mt-16 fade-in delay-500" data-id="element-38">
          <h3 className="text-2xl font-display font-bold text-center mb-6 fade-in" data-id="element-39">
            {t.featuredStockPitch}{' '}
            <span className="gradient-text" data-id="element-40">Stock Pitch</span>
          </h3>
          <div className="embed-container" data-id="element-41">
            <iframe src="https://drive.google.com/file/d/1q19z9lHsqAQtf-WYXIylRENHNE0mcs_L/preview" allow="autoplay" title="Stock Pitch" data-id="element-42"></iframe>
          </div>
        </div>
      </div>
    </section>;
};
export default About;